import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useBlocker } from "@tanstack/react-router";

/* ──────────────────────────────────────────────────────────────────────────
   Save or cancel, as Discord's role settings do it (Jay, 2026-10-15): typing
   saves nothing; once anything differs from what is saved a bar rises from
   the bottom with Cancel and Save. Trying to leave first (another page, the
   day stepper, a pasted message for another day) is refused: the page shakes,
   the bar turns red and the window's edges glow red inward (Jay, 2026-10-22).
   Closing the tab gets the browser's own prompt.
   ────────────────────────────────────────────────────────────────────────── */

const SHAKE: Keyframe[] = [0, -7, 6, -5, 4, -2, 1, 0].map((x) => ({ transform: `translateX(${x}px)` }));
const ALARM_MS = 2600;

/** Holds the person on the page while `dirty`; `nudge` is the shake and the red. */
export function useUnsaved(dirty: boolean, page: RefObject<HTMLElement | null>) {
  const [alarm, setAlarm] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const dirtyNow = useRef(dirty);
  dirtyNow.current = dirty;

  const nudge = useCallback(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!reduced) page.current?.animate(SHAKE, { duration: 420, easing: "ease-out" });
    setAlarm(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setAlarm(false), ALARM_MS);
  }, [page]);

  useEffect(() => () => clearTimeout(timer.current), []);
  // Saved or cancelled: nothing to be red about.
  useEffect(() => {
    if (!dirty) setAlarm(false);
  }, [dirty]);

  useBlocker({
    shouldBlockFn: () => {
      if (!dirtyNow.current) return false;
      nudge();
      return true;
    },
    enableBeforeUnload: () => dirtyNow.current,
  });

  return { alarm, nudge };
}

export function SaveBar({ open, alarm, message, saving, canSave, onCancel, form }: {
  open: boolean;
  alarm: boolean;
  message: string;
  saving: boolean;
  canSave: boolean;
  onCancel: () => void;
  /** The form whose submit is Save, so Enter in a box saves too. */
  form: string;
}) {
  return (
    // A zero-height dock stuck to the window's foot, so the bar sits over the page's column and takes no room.
    <div className="subs-savebar-dock">
      {/* On the body: the shake is a transform on the page, which would pin a fixed child to the page instead of the window. */}
      {createPortal(<div className={`subs-alarm-glow${open && alarm ? " on" : ""}`} aria-hidden="true" />, document.body)}
      <div className={`subs-savebar${open ? " open" : ""}${alarm ? " alarm" : ""}`} role="region" aria-label="Unsaved changes" aria-hidden={!open} inert={!open}>
        <span className="subs-savebar-text" aria-live="polite">{message}</span>
        <span className="subs-savebar-actions">
          <button type="button" className="text-button" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={form} className="button brass" disabled={!canSave || saving}>
            {saving ? "Saving" : "Save"}
          </button>
        </span>
      </div>
    </div>
  );
}
